/**
 * CLOUDWEB-20913 / CLOUDWEB-21024: debug-лог SW в IndexedDB `webpush-debug-sw`.
 *
 * Safari SW-инспектор ненадёжно отдаёт event-логи (а на iOS его может не быть под рукой
 * вовсе), поэтому дублируем ключевые события пуша в IDB — оттуда их достаёт
 * `app.webPush.logs()` со страницы (IDB общая на origin).
 *
 * Пишем только при включённой Omicron-фиче `webpush-debug`. Фича в SW доступна асинхронно
 * (postMessage + IDB-кэш, см. `serviceWorker/omicronConfig`), поэтому запись всегда
 * fire-and-forget: call-site'ы остаются синхронными, а промах гейта просто ничего не пишет.
 */
import { getSwOmicronConfig } from 'serviceWorker/omicronConfig';
import { ensureStore, MAX_DEBUG_LOG_ENTRIES, SW_DEBUG_LOG_DB, trimStore } from 'serviceWorker/swIdbSchema';

// Монотонный seq: разбивает записи, попавшие в одну миллисекунду, и держит порядок вызовов (ключ сортировки ленты — ts+seq)
// Зеркалит page-версию в webPush.deeplink.ts
let debugSeq = 0;
// Подрезаем один раз за жизнь воркера: считать размер на каждой записи незачем, а стартует
// воркер часто (его поднимает каждый пуш) — этого хватает, чтобы лента не разрасталась.
let trimDone = false;

function writeEntry(entry: Record<string, unknown>): Promise<void> {
    return new Promise((resolve) => {
        try {
            const request = indexedDB.open(SW_DEBUG_LOG_DB.name, SW_DEBUG_LOG_DB.version);

            request.onupgradeneeded = () => {
                ensureStore(request.result, SW_DEBUG_LOG_DB);
            };
            request.onsuccess = () => {
                try {
                    const db = request.result;
                    const tx = db.transaction(SW_DEBUG_LOG_DB.store, 'readwrite');

                    tx.objectStore(SW_DEBUG_LOG_DB.store).add(entry);

                    if (!trimDone) {
                        trimDone = true;
                        trimStore(db, SW_DEBUG_LOG_DB, MAX_DEBUG_LOG_ENTRIES);
                    }

                    tx.oncomplete = () => {
                        db.close();
                        resolve();
                    };
                    tx.onerror = () => {
                        db.close();
                        resolve();
                    };
                } catch (_) {
                    resolve();
                }
            };
            request.onerror = () => resolve();
        } catch (_) {
            resolve();
        }
    });
}

/**
 * Записать событие SW в debug-лог, если включена фича `webpush-debug`. Никогда не бросает.
 *
 * Возвращает промис завершения записи. Подавляющему большинству вызывающих он не нужен — запись
 * остаётся fire-and-forget, call-site'ы синхронные. Ждать его стоит там, где воркер вот-вот
 * заморозят: в `notificationclick` iOS переводит PWA на передний план сразу после тапа, и
 * незавершённая запись просто теряется — а вместе с ней и возможность понять, приходило ли
 * событие вообще.
 */
export const swDebugLog = (entry: Record<string, unknown>): Promise<void> => {
    // ts и seq снимаем синхронно, в момент события: гейт по фиче и open IDB асинхронны,
    // их латентность сдвинула бы метку и перемешала порядок записей.
    const ts = Date.now();
    const seq = debugSeq++;

    return getSwOmicronConfig().then((config) => {
        if (!config?.webpushDebug) {
            return;
        }

        return writeEntry({ ts, seq, ...entry });
    });
};
